import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import type { FiatPrice } from "@b2bportal/core-types";
import {
  useFilterDialog as useCoreFilterDialog,
  type FilterDialogComponentProps,
} from "@tenant/flights";
import { getPackageStateByFareId } from "@/store/packages/selectors/flightSelectors";

/**
 * Package-specific filter dialog hook for flights in the packages flow.
 * Uses package total pricing (flight + hotel + car) instead of standalone flight prices
 * for the price filter range. Should not be used for standalone flight searches.
 */
export const usePackageFlightFilterDialog = (
  props: FilterDialogComponentProps
) => {
  const coreFilterDialog = useCoreFilterDialog(props);

  // Get package pricing data by fare ID
  const packageStateByFareId = useSelector(getPackageStateByFareId);

  // Calculate package-aware price range from all fares with package pricing
  const packagePriceRange = useMemo((): {
    priceMin?: FiatPrice;
    priceMax?: FiatPrice;
  } => {
    if (!packageStateByFareId) {
      return { priceMin: undefined, priceMax: undefined };
    }

    let minPrice: number | undefined;
    let maxPrice: number | undefined;
    let currency: string | undefined;
    let currencySymbol: string | undefined;

    Object.values(packageStateByFareId).forEach((packageState) => {
      const totalPrice = packageState?.price?.totalPrice;

      if (!totalPrice?.fiat) {
        return;
      }

      const price = totalPrice.fiat.value;
      currency = totalPrice.fiat.currencyCode;
      currencySymbol = totalPrice.fiat.currencySymbol;

      if (minPrice === undefined || price < minPrice) {
        minPrice = price;
      }
      if (maxPrice === undefined || price > maxPrice) {
        maxPrice = price;
      }
    });

    if (minPrice === undefined || maxPrice === undefined) {
      return { priceMin: undefined, priceMax: undefined };
    }

    return {
      priceMin: {
        value: minPrice,
        currencyCode: currency,
        currencySymbol: currencySymbol,
      },
      priceMax: {
        value: maxPrice,
        currencyCode: currency,
        currencySymbol: currencySymbol,
      },
    };
  }, [packageStateByFareId]);

  // Calculate the effective price state to display
  // When Redux state is MAX_SAFE_INTEGER (no filter applied), use package max
  const effectivePriceState = useMemo(() => {
    if (
      coreFilterDialog.context.priceState === Number.MAX_SAFE_INTEGER &&
      packagePriceRange.priceMax
    ) {
      return packagePriceRange.priceMax.value;
    }
    return coreFilterDialog.context.priceState;
  }, [coreFilterDialog.context.priceState, packagePriceRange.priceMax]);

  // Manage local price state to properly track user interactions
  const [localPrice, setLocalPrice] = useState<number>(effectivePriceState);

  // Sync localPrice when effectivePriceState changes (e.g., when package data loads)
  useEffect(() => {
    if (effectivePriceState !== localPrice) {
      setLocalPrice(effectivePriceState);
    }
  }, [effectivePriceState, localPrice]);

  // Override updateValuesToMatchState to initialize price with max package price
  const updateValuesToMatchState = useCallback(() => {
    const effectiveMaxPrice =
      coreFilterDialog.context.priceState === Number.MAX_SAFE_INTEGER &&
      packagePriceRange.priceMax
        ? packagePriceRange.priceMax.value
        : coreFilterDialog.context.priceState;

    setLocalPrice(effectiveMaxPrice);
    coreFilterDialog.handlers.handlePriceChange({
      maxPrice: effectiveMaxPrice,
    });
    coreFilterDialog.handlers.handleStopsChange(coreFilterDialog.context.stop);
    coreFilterDialog.handlers.handleAirlineChange(
      coreFilterDialog.context.airline
    );
    coreFilterDialog.handlers.handleFareClassesChange(
      coreFilterDialog.context.fareClasses
    );
    coreFilterDialog.handlers.handleFlightNumberChange(
      coreFilterDialog.context.flightNumber
    );
    coreFilterDialog.handlers.handleOutboundTimeRangeChange(
      coreFilterDialog.context.outboundDepartureTimeRange,
      coreFilterDialog.context.outboundArrivalTimeRange
    );
    coreFilterDialog.handlers.handleReturnTimeRangeChange(
      coreFilterDialog.context.returnDepartureTimeRange,
      coreFilterDialog.context.returnArrivalTimeRange
    );
  }, [
    coreFilterDialog.handlers,
    coreFilterDialog.context.priceState,
    coreFilterDialog.context.stop,
    coreFilterDialog.context.airline,
    coreFilterDialog.context.fareClasses,
    coreFilterDialog.context.flightNumber,
    coreFilterDialog.context.outboundDepartureTimeRange,
    coreFilterDialog.context.outboundArrivalTimeRange,
    coreFilterDialog.context.returnDepartureTimeRange,
    coreFilterDialog.context.returnArrivalTimeRange,
    packagePriceRange.priceMax,
  ]);

  // Wrap handlePriceChange to update local state
  const handlePriceChange = useCallback(
    ({ maxPrice }: { maxPrice: number }) => {
      setLocalPrice(maxPrice);
      coreFilterDialog.handlers.handlePriceChange({ maxPrice });
    },
    [coreFilterDialog.handlers]
  );

  return {
    ...coreFilterDialog,
    context: {
      ...coreFilterDialog.context,
      // Override filterOptions with package-aware price range
      filterOptions: {
        ...coreFilterDialog.context.filterOptions,
        priceMin: packagePriceRange.priceMin,
        priceMax: packagePriceRange.priceMax,
      },
      // Override priceState with local state
      priceState: localPrice,
    },
    handlers: {
      ...coreFilterDialog.handlers,
      updateValuesToMatchState,
      handlePriceChange,
    },
  };
};
